import React, { useState } from 'react';
import { uploadFileToStorage, deleteFileFromStorage } from '../api';
import { 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  Button, 
  Box,
  Typography,
  CircularProgress,
  IconButton
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CloseIcon from '@mui/icons-material/Close';

const FileStorageComponent = ({ 
  onUploadSuccess,
  acceptedFileTypes,
  maxFileSize,
  folderName,
  initialFileUrl
}) => {
  const [isUploading, setIsUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const handleFileSelect = (event) => {
    setSelectedFile(event.target.files[0]);
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    try {
      setIsUploading(true);

      // If there's a previous file, delete it first
      if (initialFileUrl?.url) {
        await deleteFileFromStorage(initialFileUrl.fileName);
      }

      // Upload the new file
      const formData = new FormData();
      formData.append("file", selectedFile);
      formData.append("folderName", folderName);
      const response = await uploadFileToStorage(formData);
      const data = response.data.data;
      
      // Call the callback with the new URL and fileName
      onUploadSuccess({
        url: data.fileUrl,
        fileName: data.fileName
      });
      handleClose();
    } catch (error) {
      console.error('Error handling file:', error);
    } finally {
      setIsUploading(false);
    }
  };

  const handleClose = () => {
    setSelectedFile(null);
    setDialogOpen(false);
  };

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Button
          variant="outlined"
          onClick={() => setDialogOpen(true)}
          startIcon={<CloudUploadIcon />}
        >
          {initialFileUrl?.url ? 'Change Image' : 'Upload Image'}
        </Button>
      </Box>

      <Dialog 
        open={dialogOpen} 
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Upload Image
          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 2,
              py: 2
            }}
          >
            <input
              type="file"
              id="file-input"
              onChange={handleFileSelect}
              accept={acceptedFileTypes?.join(',')}
              style={{ display: 'none' }}
            />
            <label htmlFor="file-input">
              <Button
                variant="outlined"
                component="span"
                startIcon={<CloudUploadIcon />}
                disabled={isUploading}
              >
                Choose File
              </Button>
            </label>
            {selectedFile && (
              <Typography variant="body2" color="text.secondary">
                Selected: {selectedFile.name}
              </Typography>
            )}
            {initialFileUrl && (
              <Typography variant="body2" color="warning.main">
                Note: This will replace the existing file
              </Typography>
            )}
            {isUploading && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <CircularProgress size={24} />
                <Typography>Uploading...</Typography>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button 
            onClick={handleUpload}
            disabled={!selectedFile || isUploading}
            variant="contained"
          >
            Upload
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default FileStorageComponent;
